import { defineStore } from 'pinia';
import type {} from '../../../shared/interfaces/machine-store.interface';
import {
    cloneDeep,
    filter,
    findIndex,
    forEach,
    get,
    map,
    orderBy,
    set,
    toString,
    find,
    uniq,
    includes,
    omit,
    keys,
} from 'lodash';
import type {
    IImplementFilters,
    IImplementFiltersValues,
    IImplementGridDataItem,
    IImplementGridDataMeta,
    IImplementGridHeaders,
    IImplementStore,
} from '../../../shared/interfaces/implement-store.interface';
import type { IImplement } from '../../../shared/interfaces/implement.interface';
import { APP_IMPLEMENTS_STEPS, DISABLED_PROPERTIES } from '../../../config/form.config';
import type { IFormStep } from '../../../shared/interfaces/form-step.interface';
import type { IMachineProperty } from '../../../shared/interfaces/machine-property.interface';
import type { IFormValue } from '../../../shared/interfaces/form-value.interface';
import type { IStepItem } from '../../../shared/forms/step-navigation/step-item.interface';
import type { IFormField } from '../../../shared/forms/form-field.interface';
import type { IFormValueCommand } from '../../../shared/interfaces/form-value-command.interface';
import type { IApiErrorResponse } from '@/shared/services/api-client.service';

const initialStateImplementStore: IImplementStore = {
    loading: true,
    filters: {
        name: null,
        model: [],
        tractor_loader_only: false,
        min_base_weight: null,
    },
    filtersValues: {
        name: null,
        model: null,
        tractor_loader_only: null,
        min_base_weight: null,
        category: null,
    },
    girdData: {
        headers: [
            // { title: 'search_grid_title_id', key: 'id', sortable: false },
            { title: 'search_grid_title_name', key: 'name', sortable: false },
            {
                title: 'search_grid_title_hp_required',
                key: 'hp_required',
                sortable: false,
            },
            {
                title: 'search_grid_title_core_mount_type',
                key: 'core_mount_type',
                sortable: false,
            },
            {
                title: 'search_grid_title_hitch_options',
                key: 'hitch_options',
                sortable: false,
            },
            {
                title: 'search_grid_title_weight_base_weight',
                key: 'weight_base_weight',
                sortable: false,
            },
            {
                title: 'search_grid_title_performance_working_width',
                key: 'performance_working_width',
                sortable: false,
            },
            {
                title: 'search_grid_title_compatibility_series_specific',
                key: 'compatibility_series_specific',
                sortable: false,
            },
        ],
        meta: {
            total_pages: 0,
            total_items: 0,
            current_page: 0,
            items_per_page: 0,
        },
        items: [],
    },
    currentImplement: {
        loading: false,
        implement: {
            implement: {
                id: '',
                name: '',
                comment: '',
                description: '',
                created_at: null,
                last_modified_at: null,
                specs: [],
            },
            compatible_implements: [],
            maybe_compatible_implements: [],
        },
        currentStep: null,
        steps: APP_IMPLEMENTS_STEPS,
        currentStepIntex: '',
        models: [],
        values: {},
        updateValues: {},
        propertyItmes: [],
        propertyCategories: [],
    },
    current: {
        laoding: false,
        skipImport: false,
        properties: [],
        steps: [],
        currentStep: '',
        values: {},
        updateValues: {},
        confirmValues: {},
        disabledProperties: DISABLED_PROPERTIES,
        errors: null,
    },
};

const useImplementStore = defineStore('implements', {
    state: () => ({ ...initialStateImplementStore }),
    getters: {
        isCurrentPropertieDiasbled: (state) => {
            return (name: string): boolean => {
                return !!find(state.current.disabledProperties, (x) => x === name);
            };
        },
        selectImplementLoading: (state): boolean => state.loading,
        selectImplementFilters: (state): IImplementFilters => state.filters,
        selectImplementFiltersValues: (state): IImplementFiltersValues => state.filtersValues,
        selectImplementGridDataHeaders: (state): IImplementGridHeaders[] => state.girdData.headers,
        selectImplementGridDataItems: (state): IImplementGridDataItem[] => state.girdData.items,
        selectImplementGridDataMeta: (state): IImplementGridDataMeta => state.girdData.meta,
        selectCurrentImplement: (state): IImplement => state.currentImplement.implement,
        selectCurrentImplementLoading: (state): boolean => state.currentImplement.loading,
        selectCurrentImplementSteps: (state): IFormStep[] => state.currentImplement.steps,
        selectCurrentImplementCurrentStep: (state): IFormStep | null => state.currentImplement.currentStep,
        selectCurrentImplementCurrentStepIndex: (state): string => state.current.currentStep,
        selectCurrentImplementModels: (state): { name: string; class: string }[] => state.currentImplement.models,
        selectCurrentImplementValues: (state): IFormValue => state.current.values,
        selectCurrentImplementUpdatedValues: (state): IFormValue => state.currentImplement.updateValues,
        selectCurrentConfirmImplementValues: (state): IFormValue => state.current.confirmValues,
        selectCurrentImplementPropertyCategories: (state): string[] => {
            return state.currentImplement.propertyCategories;
        },
        selectCurrentImplementPropertyItems: (state): IMachineProperty[] => {
            return state.currentImplement.propertyItmes;
        },
        selectCurrentImplementErrors: (state): IApiErrorResponse | null => {
            return state.current.errors;
        },
    },
    actions: {
        // current
        setCurrentImplementErrors(errors: IApiErrorResponse | null) {
            this.current.errors = errors;
        },
        setCurrentLoading(loading: boolean) {
            this.current.laoding = loading;
        },
        setCurrentSkipImport(isSkipped: boolean) {
            this.current.skipImport = isSkipped;
        },
        setCurrentProperties(properties: IFormField[]) {
            this.current.properties = properties;
        },
        setCurrentDisabledProperties(disabledProperties: string[]) {
            this.current.disabledProperties = disabledProperties;
        },
        addCurrentDisabledProperties(disabledProperties: string[]) {
            const clone = cloneDeep(this.current.disabledProperties);
            const result = uniq([...clone, ...disabledProperties]);
            this.current.disabledProperties = result;
        },
        removeCurrentDisabledProperties(disabledProperties: string[]) {
            const clone = cloneDeep(this.current.disabledProperties);
            const result = filter(clone, (x) => !includes(disabledProperties, x));
            this.current.disabledProperties = result;
        },
        setCurrentSteps(steps: IStepItem[]) {
            this.current.steps = steps;
        },
        setCurrentStep(step: string) {
            this.current.currentStep = step;
        },
        updateCurrentStep(step: string, props: { key: string; value: any }[]) {
            const steps: IStepItem[] = cloneDeep(this.current.steps);
            const stepToUpdate: number = findIndex(steps, (s: IStepItem) => s.name === step);
            if (stepToUpdate !== -1) {
                forEach(props, (prop) => {
                    set(steps[stepToUpdate], prop.key, prop.value);
                });
            }
            this.current.steps = steps;
        },
        setCurrentValues(values: Record<string, unknown>) {
            const cleaned = omit(this.current.values, keys(values));
            const clone = cloneDeep(cleaned);
            this.current.values = Object.assign({}, clone, values);
        },
        setCurrentConfirmValues(values: Record<string, unknown>) {
            this.current.confirmValues = values;
        },
        resetCurrentValues() {
            this.current.values = {};
        },
        setCurrentUpdateValues(values: Record<string, unknown>) {
            this.current.updateValues = values;
        },
        resetCurrentUpdateValues() {
            this.current.updateValues = {};
        },
        // current
        setImplementLoading(loading: boolean) {
            this.loading = loading;
        },
        setImplementFilters(filters: IImplementFilters) {
            this.filters = filters;
        },
        setImplementFiltersValues(filtersValues: IImplementFiltersValues) {
            this.filtersValues = filtersValues;
        },
        resetImplementFiltersValues() {
            this.filtersValues = {
                name: '',
                category: ['All'],
                tractor_loader_only: null,
                min_base_weight: null,
                model: ['All'],
            };
        },
        setImplementFiltersValue(key: string, value: string) {
            const filtersValues: IImplementFiltersValues = cloneDeep(this.filtersValues);
            set(filtersValues, key, value);
            this.filtersValues = filtersValues;
        },
        setImplementGridDataItems(items: IImplementGridDataItem[]) {
            this.girdData.items = items;
        },
        setImplementGridDataMeta(meta: IImplementGridDataMeta) {
            this.girdData.meta = meta;
        },
        deleteImplement(id: string) {
            const items: IImplementGridDataItem[] = filter(
                cloneDeep(this.girdData.items),
                (x: IImplementGridDataItem) => x.id !== id
            );

            this.girdData.items = items;
        },
        setImplementData(implement: IImplement) {
            this.currentImplement.implement = implement;
        },
        resetImplementData() {
            this.currentImplement.implement = initialStateImplementStore.currentImplement.implement;
        },
        setCurrentImplementCurrentSteps(steps: IFormStep[]) {
            const orderSteps: IFormStep[] = cloneDeep(
                map(steps, (step) => {
                    return {
                        ...step,
                        form: map(step.form, (f) => {
                            return { ...f, items: orderBy(f.items, 'order') };
                        }),
                    };
                })
            );
            this.currentImplement.steps = orderSteps;
        },
        setCurrentImplementCurrentStep(step: IFormStep | null) {
            this.currentImplement.currentStep = step;
            this.currentImplement.currentStepIntex = toString(get(step, 'name'));
        },
        setCurrentImplementCurrentStepIndex(index: string) {
            this.currentImplement.currentStepIntex = index;
        },
        setCurrentImplementModels(models: { name: string; class: string }[]) {
            this.currentImplement.models = models;
        },
        resetCurrentImplementModels() {
            this.currentImplement.models = [];
        },
        setCurrentImplementLoading(loading: boolean) {
            this.currentImplement.loading = loading;
        },
        removeCurrentImplementCurrentValue(key: string) {
            const cloneValues = cloneDeep(this.currentImplement.values);
            forEach(cloneValues, (value, k) => {
                if (k === key) {
                    delete cloneValues[k];
                }
            });
            this.currentImplement.values = cloneValues;
        },
        updateCurrentImplementValues(data: IFormValueCommand) {
            this.currentImplement.values[data.key] = {
                value: data.value,
                type: data.type,
                item: data.item,
            };
        },
        resetCurrentImplementValues() {
            this.currentImplement.values = {};
            this.currentImplement.steps = APP_IMPLEMENTS_STEPS;
        },
        //
        updateCurrentImplementUpdatedValues(data: IFormValueCommand) {
            this.currentImplement.updateValues[data.key] = {
                value: data.value,
                type: data.type,
                item: data.item,
            };
        },
        resetCurrentImplementUpdatedValues() {
            this.currentImplement.updateValues = {};
        },
        //
        setcurrentImplementPropertyCategories(categories: string[]) {
            return (this.currentImplement.propertyCategories = categories);
        },
        resetcurrentImplementPropertyCategories() {
            return (this.currentImplement.propertyCategories = []);
        },
        setcurrentImplementPropertyItmes(items: IMachineProperty[]) {
            const mappedItems: IMachineProperty[] = cloneDeep(items);
            return (this.currentImplement.propertyItmes = mappedItems);
        },
        resetcurrentImplementPropertyItmes() {
            return (this.currentImplement.propertyItmes = []);
        },
        insertImplementType(type: string) {
            this.currentImplement.models.push({ name: type, class: type });
        },
    },
});

export default useImplementStore;
